// Compliance tile vocabulary shared by the property and tenancy slideovers.
//
// A certificate (or the deposit's prescribed information) is graded
// valid | expiring | expired | missing | na by the server, and every tile
// paints the same border, label colour and shield for that grade — the two
// slideovers must never disagree about what amber means.
//
// Only Phosphor's regular weight is loaded (see the layout), so the icons
// are `ph ph-*`: a duotone or fill class renders nothing at all.

export function statusColor(status) {
  switch (status) {
    case 'valid': return 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10'
    case 'expiring': return 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10'
    case 'expired': return 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10'
    case 'missing': return 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10'
    case 'na': return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
    default: return 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
  }
}

export function iconColor(status) {
  switch (status) {
    case 'valid': return 'text-green-600 dark:text-green-400'
    case 'expiring': return 'text-amber-600 dark:text-amber-400'
    case 'expired': return 'text-red-600 dark:text-red-400'
    case 'missing': return 'text-amber-600 dark:text-amber-400'
    default: return 'text-gray-400 dark:text-gray-500'
  }
}

export function icon(status) {
  switch (status) {
    case 'valid': return 'ph ph-shield-check'
    case 'expiring': return 'ph ph-shield-warning'
    case 'expired': return 'ph ph-shield-warning'
    case 'missing': return 'ph ph-shield-warning'
    case 'na': return ''
    default: return 'ph ph-shield'
  }
}

// The tile's second line for a certificate entry ({ status, expiry_display }).
export function expiryText(item) {
  if (!item) return ''
  switch (item.status) {
    case 'valid': return `Until ${item.expiry_display}`
    case 'expiring': return `Expires ${item.expiry_display}`
    case 'expired': return `Expired ${item.expiry_display}`
    case 'missing': return 'No certificate'
    case 'na': return item.expiry_display || 'Not applicable'
    default: return ''
  }
}

// The slideover's compliance rows carry their grade as a pill in the design
// language's five-state palette (docs/DESIGN.md): colour lives only here.
export function pillClass(status) {
  const base = 'inline-flex items-center rounded-full px-[7px] py-[2px] text-[10.5px] font-medium leading-[1.5] whitespace-nowrap'
  switch (status) {
    case 'valid': return `${base} bg-[#E3F1E7] text-[#1F6B3E]`
    case 'expiring': return `${base} bg-[#FBEBD2] text-[#8A5A0E]`
    case 'expired': return `${base} bg-[#F9E1E1] text-[#A03535]`
    case 'missing': return `${base} bg-[#FBEBD2] text-[#8A5A0E]`
    default: return `${base} bg-[#ECEBE6] text-[#4B4B48]`
  }
}

// The word in the pill. Callers whose item isn't a certificate (prescribed
// information is served, not in date) pass their own words per grade.
export function pillLabel(status, words = {}) {
  const defaults = { valid: 'In date', expiring: 'Expiring', expired: 'Expired', missing: 'Missing', na: 'Not required' }
  return words[status] || defaults[status] || ''
}
